import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getAllPermissions,
  listPermissions,
  createPermission,
  updatePermission,
  deletePermission,
} from '../../endpoints/permissions';
import { getGroupTypes, getGroupRolesByType } from '../../endpoints/groups';
import {
  PermissionRecord,
  PermissionOption,
  CreatePermissionRequest,
  UpdatePermissionRequest,
} from '../../types/permissions';
import { GroupType, GroupRole } from '../../types/group';
import AlertItem from '../../Components/AlertItem';

const Permissions: React.FC = () => {
  const [search, setSearch] = useState<string>('');
  const [delaySearch, setDelaySearch] = useState<string>('');
  const [filterGroupType, setFilterGroupType] = useState<string>('');
  const [filterPermission, setFilterPermission] = useState<string>('');
  const [scope, setScope] = useState<string>('international');
  const [records, setRecords] = useState<PermissionRecord[]>([]);
  const [editingRecord, setEditingRecord] = useState<PermissionRecord | null>(
    null
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState<string>('');
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);

  // Form state for create/edit
  const [formGroupTypeId, setFormGroupTypeId] = useState<string>('');
  const [formPermissionId, setFormPermissionId] = useState<string>('');
  const [formRoleId, setFormRoleId] = useState<string>('');
  const [formIsInternational, setFormIsInternational] = useState<number | null>(
    null
  );
  const [availableRoles, setAvailableRoles] = useState<GroupRole[]>([]);

  // Fetch all permissions for selector
  const { data: allPermissions = [] } = useApiGet<PermissionOption[]>(
    ['all-permissions'],
    () => getAllPermissions(),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Fetch group types only for selected scope
  const { data: groupTypes = [], isLoading: loadingGroupTypes } = useApiGet<
    GroupType[]
  >(['group-types', scope], () => getGroupTypes(scope), {
    enabled: true,
    retry: 1,
  });

  // Fetch permission records
  const {
    data: recordsData,
    isLoading: loadingRecords,
    refetch: refetchRecords,
  } = useApiGet<PermissionRecord[]>(
    ['permission-records', filterGroupType, filterPermission],
    () =>
      listPermissions(
        filterGroupType || undefined,
        filterPermission || undefined
      ),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Fetch roles when group type changes
  const { data: rolesData } = useApiGet<GroupRole[]>(
    ['group-roles', formGroupTypeId],
    () => getGroupRolesByType(formGroupTypeId),
    {
      enabled: !!formGroupTypeId,
      retry: 1,
    }
  );

  useEffect(() => {
    if (recordsData) {
      setRecords(recordsData);
    }
  }, [recordsData]);

  useEffect(() => {
    if (rolesData) {
      setAvailableRoles(rolesData);
    } else {
      setAvailableRoles([]);
    }
  }, [rolesData]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  // Filter records by search
  const filteredRecords = records.filter((record) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      record.permission_name.toLowerCase().includes(searchLower) ||
      record.group_type_name.toLowerCase().includes(searchLower) ||
      (record.role_name &&
        record.role_name.toLowerCase().includes(searchLower)) ||
      (record.permission_description &&
        record.permission_description.toLowerCase().includes(searchLower))
    );
  });

  // Reset group type filter when scope changes
  useEffect(() => {
    setFilterGroupType('');
  }, [scope]);

  const handleCreateClick = () => {
    setFormGroupTypeId('');
    setFormPermissionId('');
    setFormRoleId('');
    setFormIsInternational(null);
    setAvailableRoles([]);
    setIsCreateDialogOpen(true);
  };

  const handleEditClick = (record: PermissionRecord) => {
    setEditingRecord(record);
    setFormGroupTypeId(record.group_type_id);
    setFormPermissionId(record.permission_id);
    setFormRoleId(record.role_id || '');
    setFormIsInternational(record.is_international || null);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteRecordId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleGroupTypeChange = (groupTypeId: string) => {
    setFormGroupTypeId(groupTypeId);
    setFormRoleId(''); // Reset role when group type changes
  };

  const { mutate: createMutate, isPending: isCreating } = useApiSend(
    (data: CreatePermissionRequest) => createPermission(data),
    () => {
      setAlertState({
        severity: 'success',
        description: 'Permission created successfully!',
        title: 'Success',
      });
      setIsCreateDialogOpen(false);
      refetchRecords();
      setTimeout(() => setAlertState(null), 3000);
    },
    (error) => {
      console.error('Failed to create permission:', error);
      const errorMessage =
        (error as any)?.response?.data?.message ||
        error?.message ||
        'Failed to create permission. Please try again.';
      setAlertState({
        severity: 'error',
        description: errorMessage,
        title: 'Error',
      });
    },
    [['permission-records', filterGroupType, filterPermission]]
  );

  const { mutate: updateMutate, isPending: isUpdating } = useApiSend(
    (params: { id: string; data: UpdatePermissionRequest }) =>
      updatePermission(params.id, params.data),
    () => {
      setAlertState({
        severity: 'success',
        description: 'Permission updated successfully!',
        title: 'Success',
      });
      setIsEditDialogOpen(false);
      setEditingRecord(null);
      refetchRecords();
      setTimeout(() => setAlertState(null), 3000);
    },
    (error) => {
      console.error('Failed to update permission:', error);
      const errorMessage =
        (error as any)?.response?.data?.message ||
        error?.message ||
        'Failed to update permission. Please try again.';
      setAlertState({
        severity: 'error',
        description: errorMessage,
        title: 'Error',
      });
    },
    [['permission-records', filterGroupType, filterPermission]]
  );

  const { mutate: deleteMutate, isPending: isDeleting } = useApiSend(
    (id: string) => deletePermission(id),
    () => {
      setAlertState({
        severity: 'success',
        description: 'Permission deleted successfully!',
        title: 'Success',
      });
      setIsDeleteDialogOpen(false);
      setDeleteRecordId('');
      refetchRecords();
      setTimeout(() => setAlertState(null), 3000);
    },
    (error) => {
      console.error('Failed to delete permission:', error);
      const errorMessage =
        (error as any)?.response?.data?.message ||
        error?.message ||
        'Failed to delete permission. Please try again.';
      setAlertState({
        severity: 'error',
        description: errorMessage,
        title: 'Error',
      });
    },
    [['permission-records', filterGroupType, filterPermission]]
  );

  const handleCreate = () => {
    if (!formGroupTypeId || !formPermissionId) {
      setAlertState({
        severity: 'error',
        description: 'Group Type and Permission are required.',
        title: 'Validation Error',
      });
      return;
    }

    const data: CreatePermissionRequest = {
      group_type_id: formGroupTypeId,
      permission_id: formPermissionId,
      role_id: formRoleId || null,
      is_international: formIsInternational,
    };

    createMutate(data);
  };

  const handleUpdate = () => {
    if (!editingRecord) return;

    const data: UpdatePermissionRequest = {
      role_id: formRoleId || null,
      is_international: formIsInternational,
    };

    updateMutate({ id: editingRecord.id, data });
  };

  const handleDelete = () => {
    if (!deleteRecordId) return;
    deleteMutate(deleteRecordId);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      {alertState && (
        <Grid item xs={12} padding={2}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}

      <Grid item xs={12} padding={1}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" color="primary">
            Permissions Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            color="primary"
          >
            Add Permission
          </Button>
        </Box>
      </Grid>

      {/* Filters */}
      <Grid item xs={12} padding={2}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search"
                size="small"
                fullWidth
                value={delaySearch}
                onChange={(e) => setDelaySearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: delaySearch && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setDelaySearch('')}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Scope</InputLabel>
                <Select
                  value={scope}
                  label="Scope"
                  onChange={(e) => setScope(e.target.value)}
                >
                  <MenuItem value="international">International</MenuItem>
                  <MenuItem value="spd_chapter">S/P/D Chapter</MenuItem>
                  <MenuItem value="local_chapter">Local Chapter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Group Type</InputLabel>
                <Select
                  value={filterGroupType}
                  label="Group Type"
                  onChange={(e) => setFilterGroupType(e.target.value)}
                  disabled={loadingGroupTypes}
                >
                  <MenuItem value="">All Group Types</MenuItem>
                  {[...groupTypes]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Permission</InputLabel>
                <Select
                  value={filterPermission}
                  label="Permission"
                  onChange={(e) => setFilterPermission(e.target.value)}
                >
                  <MenuItem value="">All Permissions</MenuItem>
                  {allPermissions.map((permission) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      {permission.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Table */}
      <Grid item xs={12} padding={2}>
        <TableContainer component={Paper}>
          {loadingRecords ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Group Type</TableCell>
                  <TableCell>Permission</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>International</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No permissions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>{record.group_type_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {record.permission_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {record.permission_description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.role_name || '-'}</TableCell>
                      <TableCell>
                        {record.is_international !== null &&
                        record.is_international !== undefined ? (
                          <Chip
                            label={record.is_international === 1 ? 'Yes' : 'No'}
                            size="small"
                            color={
                              record.is_international === 1
                                ? 'success'
                                : 'default'
                            }
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(record)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(record.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Grid>

      {/* Create Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Permission</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={scope}
                  label="Scope"
                  onChange={(e) => {
                    setScope(e.target.value);
                    setFormGroupTypeId(''); // Reset group type when scope changes
                    setFormRoleId(''); // Reset role when scope changes
                  }}
                >
                  <MenuItem value="international">International</MenuItem>
                  <MenuItem value="spd_chapter">S/P/D Chapter</MenuItem>
                  <MenuItem value="local_chapter">Local Chapter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Group Type</InputLabel>
                <Select
                  value={formGroupTypeId}
                  label="Group Type"
                  onChange={(e) => handleGroupTypeChange(e.target.value)}
                  disabled={loadingGroupTypes || !scope}
                >
                  {[...groupTypes]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Permission</InputLabel>
                <Select
                  value={formPermissionId}
                  label="Permission"
                  onChange={(e) => setFormPermissionId(e.target.value)}
                >
                  {allPermissions.map((permission) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      {permission.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Role (Optional)</InputLabel>
                <Select
                  value={formRoleId}
                  label="Role (Optional)"
                  onChange={(e) => setFormRoleId(e.target.value)}
                  disabled={!formGroupTypeId || availableRoles.length === 0}
                >
                  <MenuItem value="">None</MenuItem>
                  {availableRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>International</InputLabel>
                <Select
                  value={
                    formIsInternational === null ? '' : formIsInternational
                  }
                  label="International"
                  onChange={(e) =>
                    setFormIsInternational(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  <MenuItem value="">Not Set</MenuItem>
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCreateDialogOpen(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={isCreating || !formGroupTypeId || !formPermissionId}
            startIcon={isCreating ? <CircularProgress size={16} /> : null}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Permission</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Group Type:</strong> {editingRecord?.group_type_name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Permission:</strong> {editingRecord?.permission_name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Role (Optional)</InputLabel>
                <Select
                  value={formRoleId}
                  label="Role (Optional)"
                  onChange={(e) => setFormRoleId(e.target.value)}
                  disabled={!formGroupTypeId || availableRoles.length === 0}
                >
                  <MenuItem value="">None</MenuItem>
                  {availableRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>International</InputLabel>
                <Select
                  value={
                    formIsInternational === null ? '' : formIsInternational
                  }
                  label="International"
                  onChange={(e) =>
                    setFormIsInternational(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  <MenuItem value="">Not Set</MenuItem>
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsEditDialogOpen(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this permission? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default Permissions;
